import { createConfigParametersRepository } from '../../configParametersRepository';
import { checkThirdPartyTrackingStorageInit } from '../../configParametersRepository/storageClient/index';
import { getCustomDimensionsFromParams } from '../google-analytics/ga4';
import type { CustomDimensionsOrParams } from '../google-analytics/ga4Types';
import { CUSTOM_PARAMS_STORAGE_KEY } from '../resources/constants';
import type { SetCustomDimensionOrParam } from '../types';
import { updateCustomParamsAndDimensions } from './storageSync';

/**
 * Update a single custom dimension or custom param. For updating multiple parameters at once, use updateCustomParams instead.
 * @param params
 * @returns void
 */
export const updateCustomParam = (params: SetCustomDimensionOrParam) => {
  checkThirdPartyTrackingStorageInit();
  const { value } = params;
  if ('customParam' in params) {
    updateCustomParamsAndDimensions({
      [params.customParam]: value,
    });
    return;
  }
  const dimensionName = `dimension${params.customDimension}`;
  updateCustomParamsAndDimensions({
    [dimensionName]: value,
  });
};

/**
 * Update multiple custom dimensions or custom params at once. For updating a single parameter, use updateCustomParam instead.
 * @param params
 * @return void
 */
export const updateCustomParams = (params: CustomDimensionsOrParams) => {
  checkThirdPartyTrackingStorageInit();
  updateCustomParamsAndDimensions(params);
};

/**
 * Returns custom dimensions from tracking storage, converted from custom params if needed. Use only for backward compatibility with UA. For GA4, use getGaCustomParams instead.
 * @returns Custom dimensions for UA compatibility. For GA4, use getGaCustomParams instead.
 * @deprecated This method will be removed in future versions. Use getGaCustomParams instead.
 */
export const getGaCustomDimensions = () => {
  const customParams = getGaCustomParams();
  return getCustomDimensionsFromParams(customParams);
};

/**
 * Returns custom params stored in tracking storage.
 * @returns Custom params for GA4.
 */
export const getGaCustomParams = () => {
  checkThirdPartyTrackingStorageInit();
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  return storageClient?.getTrackingStoredValue(CUSTOM_PARAMS_STORAGE_KEY);
};
